import {useState} from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import CustomTextField from '../common/CustomTextField';
import * as formValidations from '../../helpers/validations';
import FormLayout from "../patientSignup/FormLayout";
import './LoginForm.scss';

const defaultFormValues = {
  email: '',
  password: '',
};

const validations = {
  email: formValidations.email,
  password: formValidations.password,
};

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  const value = get(e, 'target.value', '');
  const values = {
    ...formValues,
    [fieldName]: fieldName === 'email' ? value.toLowerCase() : value,
  };
  return setFormValues(values);
};

const checkIfFormIsValid = (
  formValues,
  validationDict,
) => {
  const containsInvalidValues = Object.keys(validationDict).reduce((acc, k) => (
    validationDict[k] ? acc : false
  ), true);
  return containsInvalidValues;
};

const LoginForm = props => {
  const {onSubmit} = props;
  const [formValues, setFormValues] = useState(
    props.defaultEmail 
    ? {...defaultFormValues, email: props.defaultEmail}
    : defaultFormValues
  );
  const [blurred, setBlurred] = useState(props.defaultEmail ? {email: true} : {});
  const navigate = useNavigate();
  const onChangeHandler = handleFieldUpdate(formValues, setFormValues);
  const validationDict = Object.keys(validations).reduce((acc, k) => ({
    ...acc,
    [k]: validations[k](formValues[k])
  }), {});
  const formIsValid = checkIfFormIsValid(formValues, validationDict);
  return (
    <FormLayout className='LoginForm'>
      {
        props.error
          ? <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            {props.error.message}
          </Alert>
          : <></>
      }
      <CustomTextField
        id='emailInput'
        placeholder='jane.smith@gmail.com'
        variant='outlined'
        label='Email'
        required
        autoFocus={true}
        type='email'
        defaultValue={props.defaultEmail || ''}
        error={!validationDict.email && blurred.email}
        onBlur={() => setBlurred({...blurred, email: true})}
        onChange={onChangeHandler('email')}
      />
      <CustomTextField
        id='passwordInput'
        placeholder='Enter a password'
        variant='outlined'
        label='Password'
        required
        type='password'
        error={!validationDict.password && blurred.password}
        onBlur={() => setBlurred({...blurred, password: true})}
        onChange={onChangeHandler('password')}
        onKeyPress={e => {
          if (e.key === 'Enter' && formIsValid) {
            onSubmit(formValues);
          }
        }}
        helperText={
          !validationDict.password
            ? 'Password must be at least 8 characters and contain an uppercase letter, a lowercase letter, and a number.'
            : null
        }
      />

      <Stack direction='column' gap={2}>
        <Button
          className='LoginForm__login-button'
          variant='primaryGradient'
          size='medium'
          disabled={!formIsValid}
          onClick={() => onSubmit(formValues)}
        >
          Login
        </Button>

        <Button size='medium'
                variant='primaryOutline'
                className='LoginForm__reset-password-button'
                onClick={() => navigate('/reset-password/start')}
        >
          Reset Password
        </Button>
      </Stack>

      <Typography variant='small500' lineHeight='1rem' textAlign='center' marginTop={1}>
          Don't have an account yet?
        {' '}<Link className='LoginForm__dont-have-account-link' onClick={() => navigate('/signup')}>Sign up</Link>
      </Typography>
    </FormLayout>
  );
};

LoginForm.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  onSubmit: PropTypes.func, // ({email, password}) => { doSomething() }
}

export default LoginForm;
